import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import "./Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [otpChallenge, setOtpChallenge] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [otp, setOtp] = useState("");
  const { login, verifyElectionLoginOTP } = useAuth();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const authMessage = sessionStorage.getItem("tarewu_auth_message");
    if (authMessage) {
      setError(authMessage);
      sessionStorage.removeItem("tarewu_auth_message");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (otpChallenge) {
        await verifyElectionLoginOTP(otpChallenge, otp);
        navigate("/elections");
      } else {
        const result = await login(email, password);
        if (result?.otp_required) {
          setOtpChallenge(result.challenge);
          setPhoneHint(result.phone_hint || "your registered phone");
          setPassword("");
        } else {
          navigate("/");
        }
      }
    } catch (err) {
      setError(err.message || t("Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-language-row">
          <button
            type="button"
            onClick={toggleLanguage}
            className="login-language-btn"
          >
            {language === "en" ? "EN" : "SW"}
          </button>
        </div>
        <div className="login-header">
          <div className="login-logo">
            <img
              src="/TAREWU_logo-2.png"
              alt="TAREWU"
              style={{ width: "80px", height: "80px", objectFit: "contain" }}
            />
          </div>
          <h1 className="login-title">TANZANIA REVENUE WORKERS UNION</h1>
          <p className="login-subtitle">{t("TAREWU Management System")}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          {!otpChallenge && (
            <div className="form-group">
              <label className="form-label">{t("Email")}</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">@</span>
                <input
                  type="email"
                  className="form-input"
                  placeholder={t("Enter your email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            </div>
          )}

          {!otpChallenge && (
            <div className="form-group">
              <label className="form-label">{t("Password")}</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">●</span>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-input"
                  placeholder={t("Enter your password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? t("Hide") : t("Show")}
                </button>
              </div>
            </div>
          )}

          {otpChallenge && (
            <div className="form-group election-login-otp">
              <label className="form-label">Election login OTP</label>
              <p>
                We sent a 6-digit code to {phoneHint}. It expires in 10 minutes.
              </p>
              <div className="login-input-wrap">
                <span className="login-input-icon">#</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength="6"
                  className="form-input"
                  placeholder="••••••"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              <button
                type="button"
                className="login-change-account"
                onClick={() => {
                  setOtpChallenge("");
                  setOtp("");
                  setError("");
                }}
              >
                Use another account
              </button>
            </div>
          )}

          {!otpChallenge && (
            <p className="login-forgot"><Link to="/forgot-password">{t("Forgot password?")}</Link></p>
          )}

          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={loading}
          >
            {loading
              ? t("Please wait...")
              : otpChallenge
                ? "Verify & Sign In"
                : t("Sign In")}
          </button>
        </form>

        <div className="login-footer">
          <span>🔒</span>
          <p>{t("Your account is protected by secure authentication.")}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
